import React, { useEffect, useMemo, useRef, useState } from 'react';
import { HomeIcon, HomeModernIcon } from '@heroicons/react/16/solid';
import { homeService } from '../clients/ApiClients';
import { HoaReturn, HomeReturn } from '../clients/rust-api-client';
import { apiErrorMessage } from '../utils/apiErrors';
import { PropertyPicker } from '../violation/linear/PropertyPicker';
import { homeAddressLine } from '../home/homeMeta';
import { useSyncedHomes } from '../sync/hooks';

/**
 * The "where" of a create (#632), as the first chips in the modal's
 * property row: association, then home. Replaces the separate "Which
 * home?" step quick-create used to have — the modal asks for it in place,
 * with the same pickers as every other property.
 *
 * A chip the page fixed (the home page's own home, say) is `locked`:
 * shown, not changeable, because the list behind the modal is that
 * home's and a violation for another home would appear in the wrong list.
 *
 * Opened for a member (their page, or a list of their violations), the
 * chips offer only what is theirs: their associations and their homes,
 * and the one answer is chosen when there is only one.
 */

/**
 * One association's list of something — homes, members, vendors — loaded
 * when the association is chosen and dropped when it changes. `fetch` must
 * be stable (module-level), or this refetches every render.
 *
 * `synced` is the sync engine's copy of the same list (#561), already
 * narrowed to `hoaId`: `null` while the pool has none, and then this
 * fetches, exactly as the views that read the pool do.
 */
export function useForHoa<T>(
  hoaId: string | null | undefined,
  fetch: (hoaId: string) => Promise<T[]>,
  what: string,
  synced: T[] | null = null
): { items: T[]; loading: boolean; error: string | null } {
  const [items, setItems] = useState<T[]>([]);
  // Which association the current items (or error) are for. Loading is
  // "not yet answered for this one", so the render right after a change
  // never passes off an empty list as the answer.
  const [answeredFor, setAnsweredFor] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const haveSynced = synced !== null;

  useEffect(() => {
    setItems([]);
    setError(null);
    setAnsweredFor(null);
    if (!hoaId || haveSynced) return;
    let live = true;
    fetch(hoaId)
      .then((list) => live && setItems(list))
      .catch(
        (err) =>
          live && setError(apiErrorMessage(err, `Couldn't load ${what}.`))
      )
      .finally(() => live && setAnsweredFor(hoaId));
    return () => {
      live = false;
    };
  }, [hoaId, fetch, what, haveSynced]);

  if (synced !== null) {
    return { items: hoaId ? synced : [], loading: false, error: null };
  }
  return { items, loading: !!hoaId && answeredFor !== hoaId, error };
}

/**
 * The association and home a modal is creating for: reset from `scope`
 * each time it opens, the home cleared when the association changes, and
 * the only association picked for you when there is just one.
 */
export function useScopeState(
  open: boolean,
  scope: { hoaId?: string | null; homeId?: string | null },
  hoas: HoaReturn[]
) {
  const [hoaId, setHoaIdRaw] = useState<string | null>(null);
  const [homeId, setHomeId] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setHoaIdRaw(scope.hoaId || null);
    setHomeId(scope.homeId || null);
    // Only on open: the scope object is rebuilt on every render upstream.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // One association is the common case for a manager: skip the question.
  useEffect(() => {
    if (open && !hoaId && hoas.length === 1) setHoaIdRaw(hoas[0].id);
  }, [open, hoaId, hoas]);

  const setHoaId = (id: string) => {
    setHoaIdRaw(id);
    setHomeId(null);
  };
  return { hoaId, setHoaId, homeId, setHomeId };
}

const byAddress = (homes: HomeReturn[]): HomeReturn[] =>
  [...homes].sort((a, b) =>
    homeLabel(a).localeCompare(homeLabel(b), undefined, { numeric: true })
  );

const fetchHomes = async (hoaId: string): Promise<HomeReturn[]> =>
  byAddress((await homeService.getHomesByHoa({ hoaId })).data);

const fetchMemberHomes = async (memberId: string): Promise<HomeReturn[]> =>
  byAddress((await homeService.getHomesByMember({ memberId })).data);

export const homeLabel = (h: HomeReturn): string => homeAddressLine(h) || h.id;

/** The picker's value for "no home": the work order is association-wide. */
const NO_HOME = '__none__';

interface Props {
  hoas: HoaReturn[];
  hoasLoading?: boolean;
  hoaId: string | null;
  onHoaChange: (hoaId: string) => void;
  lockHoa?: boolean;
  /** Offer only this member's associations and homes. */
  memberId?: string | null;
  /** Omit to show the association chip alone (a message to an HOA). */
  home?: {
    homeId: string | null;
    onChange: (homeId: string | null) => void;
    locked?: boolean;
    /** A work order may be for the association as a whole. */
    optional?: boolean;
  };
}

export const ScopeChips: React.FC<Props> = ({
  hoas,
  hoasLoading,
  hoaId,
  onHoaChange,
  lockHoa,
  memberId,
  home,
}) => {
  const syncedHomes = useSyncedHomes({ hoaId });
  const sortedSynced = useMemo(
    () => (syncedHomes ? byAddress(syncedHomes) : null),
    [syncedHomes]
  );
  const hoaHomes = useForHoa(
    home && !memberId ? hoaId : null,
    fetchHomes,
    'homes',
    sortedSynced
  );
  // A member's homes in every association, so the association chip can
  // offer only theirs too. useForHoa is keyed by any id; here, the member.
  const syncedMine = useSyncedHomes({ memberId });
  const sortedMine = useMemo(
    () => (memberId && syncedMine ? byAddress(syncedMine) : null),
    [memberId, syncedMine]
  );
  const mine = useForHoa(memberId, fetchMemberHomes, 'homes', sortedMine);
  const homes = memberId
    ? { ...mine, items: mine.items.filter((h) => h.hoa_id === hoaId) }
    : hoaHomes;
  const hoaOptions = useMemo(() => {
    if (!memberId || mine.loading || mine.error) return hoas;
    const theirs = new Set(mine.items.map((h) => h.hoa_id));
    return hoas.filter((h) => theirs.has(h.id));
  }, [hoas, memberId, mine.loading, mine.error, mine.items]);

  // For a member there is usually one answer: take it. The association
  // only while none is chosen; the home once per association, so a work
  // order set back to "No home" stays that way.
  const onlyHoa =
    memberId && !lockHoa && !hoaId && hoaOptions.length === 1
      ? hoaOptions[0].id
      : null;
  useEffect(() => {
    if (onlyHoa) onHoaChange(onlyHoa);
  }, [onlyHoa, onHoaChange]);
  const pickedFor = useRef<string | null>(null);
  const onlyHome =
    memberId && home && !home.locked && !homes.loading && homes.items.length === 1
      ? homes.items[0].id
      : null;
  useEffect(() => {
    if (!onlyHome || !hoaId || pickedFor.current === hoaId) return;
    pickedFor.current = hoaId;
    if (!home?.homeId) home?.onChange(onlyHome);
    // `home` is rebuilt on every render upstream; the guard above is what
    // keeps this to once per association.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onlyHome, hoaId]);

  const hoa = hoas.find((h) => h.id === hoaId);
  const chosenHome = homes.items.find((h) => h.id === home?.homeId);
  const muted = (text: string) => <span className="text-fg-muted">{text}</span>;

  const homeText = (): React.ReactNode => {
    if (!home) return null;
    if (chosenHome) return homeLabel(chosenHome);
    if (home.homeId)
      return homes.loading ? muted('Loading home…') : muted('Home');
    if (!hoaId) return muted('Home');
    if (homes.loading) return muted('Loading homes…');
    if (homes.error) return <span className="text-danger">{homes.error}</span>;
    if (memberId && homes.items.length === 0) return muted('No home of theirs here');
    return muted(home.optional ? 'No home' : 'Home');
  };

  return (
    <>
      <PropertyPicker
        options={hoaOptions.map((h) => ({ value: h.id, label: h.name }))}
        value={hoaId}
        onChange={(id) => id !== hoaId && onHoaChange(id)}
        placeholder="Association…"
        ariaLabel={hoa ? `Association: ${hoa.name}` : 'Choose association'}
        readOnly={lockHoa || hoasLoading}
        trigger={
          <>
            <HomeModernIcon className="h-3.5 w-3.5 text-fg-muted" aria-hidden />
            {hoa
              ? hoa.name
              : muted(hoasLoading ? 'Loading associations…' : 'Association')}
          </>
        }
      />
      {home && (
        <PropertyPicker
          options={[
            ...(home.optional
              ? [{ value: NO_HOME, label: 'No home — the whole association' }]
              : []),
            ...homes.items.map((h) => ({ value: h.id, label: homeLabel(h) })),
          ]}
          value={home.homeId ?? (home.optional ? NO_HOME : null)}
          onChange={(id) => home.onChange(id === NO_HOME ? null : id)}
          placeholder="Address…"
          ariaLabel={
            chosenHome
              ? `Home: ${homeLabel(chosenHome)}`
              : hoaId
                ? 'Choose home'
                : 'Choose an association first'
          }
          readOnly={home.locked || !hoaId || homes.loading}
          trigger={
            <>
              <HomeIcon className="h-3.5 w-3.5 text-fg-muted" aria-hidden />
              {homeText()}
            </>
          }
        />
      )}
    </>
  );
};
